import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from '../../../services/api.service';
import { ApiUrls } from '../../../constants/api-urls';
import { readReferralCode } from '../../../shared/referral-code.util';

type BillingTerm = 'monthly' | 'quarterly' | 'semiAnnual' | 'annual';

type ReviewSetupPlanState = {
  planId: string | null;
  tierCode: string;
  planName: string;
  price: number;
  billingTerm: BillingTerm;
  pricePerMonth: number;
  totalPrice: number;
  currencySymbol: string;
  features: string[];
};

interface BusinessSummary {
  name: string;
  address: string;
  city: string;
  zipCode: string;
  countryName: string;
  stateName: string | null;
  crn: string;
  status: string;
}

const BILLING_LABELS: Record<BillingTerm, string> = {
  monthly: 'Billed monthly',
  quarterly: 'Billed every 3 months',
  semiAnnual: 'Billed every 6 months',
  annual: 'Billed annually',
};

const SELECTED_PLAN_KEY = 'icip_selected_plan';

const DEFAULT_PLAN_STATE: ReviewSetupPlanState = {
  planId: null,
  tierCode: 'GROWTH',
  planName: 'Growth',
  price: 900,
  pricePerMonth: 900,
  totalPrice: 900,
  billingTerm: 'monthly',
  currencySymbol: '€',
  features: [
    'Branded calling for 7,500 calls per period',
    'Custom Display Name',
    'Advanced analytics & reporting',
    'Priority email & chat support',
    'Portal self-service management',
    'API access',
  ],
};

@Component({
  selector: 'app-review-setup',
  standalone: false,
  templateUrl: './review-setup.component.html',
  styleUrl: './review-setup.component.scss',
})
export class ReviewSetupComponent implements OnInit {
  plan: ReviewSetupPlanState = DEFAULT_PLAN_STATE;
  business: BusinessSummary | null = null;
  businessLoading = true;
  // Referral code captured from a shared ?code= link; applied at checkout (not validated here).
  referralCode: string | null = null;

  constructor(
    private router: Router,
    private api: ApiService,
  ) {}

  ngOnInit(): void {
    this.loadPlan();
    this.loadBusiness();
    this.referralCode = readReferralCode();
  }

  private loadPlan(): void {
    const cached = sessionStorage.getItem(SELECTED_PLAN_KEY);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        this.plan = {
          planId: parsed.planId ?? null,
          tierCode: parsed.tierCode ?? '',
          planName: parsed.planName ?? DEFAULT_PLAN_STATE.planName,
          price: parsed.pricePerMonth ?? parsed.price ?? DEFAULT_PLAN_STATE.price,
          pricePerMonth: parsed.pricePerMonth ?? DEFAULT_PLAN_STATE.pricePerMonth,
          totalPrice: parsed.totalPrice ?? DEFAULT_PLAN_STATE.totalPrice,
          billingTerm: parsed.billingTerm ?? DEFAULT_PLAN_STATE.billingTerm,
          currencySymbol: parsed.currencySymbol ?? DEFAULT_PLAN_STATE.currencySymbol,
          features: parsed.features ?? DEFAULT_PLAN_STATE.features,
        };
        return;
      } catch {
        // fall through to router state
      }
    }

    const navState = this.router.getCurrentNavigation()?.extras?.state as Partial<ReviewSetupPlanState> | undefined;
    if (navState?.planName) {
      this.plan = {
        ...DEFAULT_PLAN_STATE,
        ...navState,
        price: navState.price ?? navState.pricePerMonth ?? DEFAULT_PLAN_STATE.price,
      };
    }
  }

  private loadBusiness(): void {
    this.api.get(ApiUrls.User.GET_BUSINESS).subscribe({
      next: (res) => {
        const b = res?.data;
        if (b) {
          this.business = {
            name: b.name ?? '',
            address: b.address ?? '',
            city: b.city ?? '',
            zipCode: b.zipCode ?? '',
            countryName: b.country?.name ?? b.countryCode ?? '',
            stateName: b.state?.name ?? null,
            crn: b.crn ?? '',
            status: b.businessStatus ?? '',
          };
        }
        this.businessLoading = false;
      },
      error: () => {
        this.businessLoading = false;
      },
    });
  }

  get billingLabel(): string {
    return BILLING_LABELS[this.plan.billingTerm];
  }

  get businessAddressLine(): string {
    if (!this.business) return '';
    const parts = [this.business.city];
    if (this.business.stateName) parts.push(this.business.stateName);
    if (this.business.zipCode) parts.push(this.business.zipCode);
    return parts.filter(Boolean).join(', ');
  }

  trackByString(_idx: number, value: string): string {
    return value;
  }

  formatPrice(value: number): string {
    const decimals = Number.isInteger(value) ? 0 : 2;
    try {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(value);
    } catch {
      return value.toFixed(decimals);
    }
  }

  proceedToCheckout(): void {
    this.router.navigate(['/user/checkout']);
  }
}
